import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  children: JSX.Element;
  /** Also require the `access` claim (set after redeeming an access code). */
  requireAccess?: boolean;
}

export function ProtectedRoute({ children, requireAccess = false }: Props): JSX.Element {
  const { t } = useTranslation();
  const { token, status, access, user } = useAuth();
  const location = useLocation();

  // We have a token but are still resolving /me — show a minimal "checking sign-in"
  // state instead of flashing the protected child (which would otherwise render
  // briefly and then redirect once fetchCurrentUser rejects an expired token).
  if (token && status === 'verifying') {
    return (
      <main className="results-loading" role="status" aria-live="polite">
        {t('auth.checkingSignIn')}
      </main>
    );
  }

  // Boot-time guest-login in flight — same UX as the verifying case so we don't
  // flash a redirect before the guest JWT lands.
  if (!token && status === 'loading' && !user) {
    return (
      <main className="results-loading" role="status" aria-live="polite">
        {t('auth.settingUpSession')}
      </main>
    );
  }

  // No token (either never signed in, or fetchCurrentUser cleared it because
  // the token was invalid/expired). Redirect — but only forward pathname+search
  // so we don't leak the entire history.state into the next route.
  if (!token && status !== 'authenticated') {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: { pathname: location.pathname, search: location.search } }}
      />
    );
  }

  // Logged in (real OR guest) but lacking access — bounce to /profile so they
  // can redeem a code, NOT to /login (they're already authenticated).
  if (requireAccess && !access) {
    return (
      <Navigate
        to="/profile"
        replace
        state={{
          from: { pathname: location.pathname, search: location.search },
          needAccess: true,
        }}
      />
    );
  }

  return children;
}
